/**
 * Sniper — pure, framework-free helpers shared by the Sniper page.
 *
 * The ARM predicate is the FROZEN contract logic (`sniper-contract.md` §counter
 * enum + arm predicate), mirrored EXACTLY from the control-plane
 * `sniper.arm.ts::isArmed` and the Rust `sniper_types.rs::is_armed`:
 *
 *     armed ⇔ count(counter, state) == floor(line)
 *
 * `line` is a half-integer (`X.5`) config-time invariant, so `count ==
 * floor(line)` means the very next increment pushes the total over the line →
 * Over resolves YES. Team-scoped counters (`team_*`) resolve `count` against
 * that team only; with no team a `team_*` count is unresolvable → NOT armed
 * (fail-closed).
 *
 * `MatchState` is DERIVED from the live match-events feed (the same incidents
 * the engine classifies) — never fabricated. Score/corner counts come from
 * `deriveMatchSummary` (the exact derivation the read-only Live-match-events tab
 * already uses), so the badge and the engine read one source of truth.
 */

import type {
  SniperCounter,
  SniperTeam,
  MatchState,
  SniperMarketCandidate,
  SniperMarketDraft,
} from '../types/sniper.types';
import type { MatchEventFrame } from '../types/matchEvents.types';
import {
  deriveMatchSummary,
  type MatchSummary,
} from './matchEventsFeed';

// ─── arm predicate (contract §counter enum + arm predicate) ──────────────────

/**
 * The counter value for a market in the current state. Returns `null` when the
 * count is unresolvable (a `team_*` counter with no team) — fail-closed.
 */
export function count(
  counter: SniperCounter,
  state: MatchState,
  team?: SniperTeam | null,
): number | null {
  switch (counter) {
    case 'match_goals':
      return state.homeScore + state.awayScore;
    case 'match_corners':
      return state.homeCorners + state.awayCorners;
    case 'team_goals':
      if (team === 'home') return state.homeScore;
      if (team === 'away') return state.awayScore;
      return null;
    case 'team_corners':
      if (team === 'home') return state.homeCorners;
      if (team === 'away') return state.awayCorners;
      return null;
    default:
      return null;
  }
}

/**
 * `armed ⇔ count == floor(line)`. Half-integer `line` is a config-time
 * invariant; this predicate uses `Math.floor(line)` and does not re-validate.
 * A `team_*` counter with no team is never armed.
 */
export function isArmed(
  counter: SniperCounter,
  state: MatchState,
  line: number,
  team?: SniperTeam | null,
): boolean {
  const c = count(counter, state, team);
  if (c === null) return false;
  return c === Math.floor(line);
}

// ─── MatchState derivation from the live feed ────────────────────────────────

/** The latest non-null game-clock seconds + period id across the frame buffer. */
function deriveClockPeriod(frames: MatchEventFrame[]): {
  clock: number | null;
  period: number | null;
} {
  let latest: MatchEventFrame | null = null;
  for (const f of frames) {
    if (f.seconds == null) continue;
    if (
      !latest ||
      new Date(f.receivedAt).getTime() >= new Date(latest.receivedAt).getTime()
    ) {
      latest = f;
    }
  }
  return { clock: latest?.seconds ?? null, period: latest?.period?.id ?? null };
}

/**
 * Build a `MatchState` from a pre-computed `MatchSummary` (goals/corners
 * totals) plus the raw frames (for clock/period/VAR). Split out so the summary
 * — already memoised by the page — is reused rather than recomputed.
 */
export function matchStateFromSummary(
  summary: MatchSummary,
  frames: MatchEventFrame[],
): MatchState {
  const { clock, period } = deriveClockPeriod(frames);
  const varActive = frames.some(
    (f) => f.category === 'var' && f.incidentId === 1074,
  );
  return {
    homeScore: summary.goals?.total.home ?? 0,
    awayScore: summary.goals?.total.away ?? 0,
    homeCorners: summary.corners?.total.home ?? 0,
    awayCorners: summary.corners?.total.away ?? 0,
    clock,
    period,
    lastVarActive: varActive,
  };
}

/** Convenience: derive the full `MatchState` straight from a frame buffer. */
export function deriveMatchState(frames: MatchEventFrame[]): MatchState {
  return matchStateFromSummary(deriveMatchSummary(frames), frames);
}

// ─── subgroup (trigger-type) ordering ────────────────────────────────────────

/** A market's trigger-type subgroup — the page groups rows under these. */
export type SniperSubgroup = 'goals' | 'corners';

/** Subgroup display order (Gol before Corner) + their Spanish labels. */
export const SNIPER_SUBGROUP_ORDER: SniperSubgroup[] = ['goals', 'corners'];

export const SNIPER_SUBGROUP_LABEL: Record<SniperSubgroup, string> = {
  goals: 'Evento: Gol',
  corners: 'Evento: Corner',
};

/** Map a counter to its trigger-type subgroup. */
export function subgroupForCounter(counter: SniperCounter): SniperSubgroup {
  return counter === 'match_goals' || counter === 'team_goals'
    ? 'goals'
    : 'corners';
}

/**
 * Partition a list of counter-carrying rows into ordered subgroups
 * (goals first, then corners). Empty subgroups are omitted.
 */
export function groupBySubgroup<T extends { counter: SniperCounter }>(
  rows: T[],
): Array<{ subgroup: SniperSubgroup; label: string; rows: T[] }> {
  return SNIPER_SUBGROUP_ORDER.map((subgroup) => ({
    subgroup,
    label: SNIPER_SUBGROUP_LABEL[subgroup],
    rows: rows.filter((r) => subgroupForCounter(r.counter) === subgroup),
  })).filter((g) => g.rows.length > 0);
}

// ─── formatters ──────────────────────────────────────────────────────────────

/** Human label for a counter (venue-agnostic). */
export function counterLabel(counter: SniperCounter): string {
  switch (counter) {
    case 'match_goals':
      return 'Match goals';
    case 'team_goals':
      return 'Team goals';
    case 'match_corners':
      return 'Match corners';
    case 'team_corners':
      return 'Team corners';
    default:
      return counter;
  }
}

/** Short venue chip label. */
export function venueLabel(venue: string): string {
  if (venue === 'kalshi') return 'Kalshi';
  if (venue === 'polymarket') return 'Polymarket';
  return venue;
}

/** Venue chip accent colour (Kalshi teal, Polymarket indigo). */
export function venueColor(venue: string): string {
  if (venue === 'kalshi') return '#00b8a3';
  if (venue === 'polymarket') return '#5b6ef5';
  return '#9e9e9e';
}

/** `over 2.5 (home)` style summary of a market's target. */
export function targetSummary(
  counter: SniperCounter,
  line: number,
  team: SniperTeam | null,
): string {
  const base = `${counterLabel(counter)} Over ${line}`;
  return team ? `${base} · ${team}` : base;
}

/** Dollars → cents string, e.g. 0.99 → "99¢", 0.9 → "90¢". Null → "—". */
export function priceToCents(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(price)) return '—';
  return `${Math.round(price * 100)}¢`;
}

/** Confidence-gate 0..1 → integer percent (clamped). */
export function gatePct(gate: number | null | undefined): number {
  if (gate == null || !Number.isFinite(gate)) return 0;
  return Math.max(0, Math.min(100, Math.round(gate * 100)));
}

/**
 * The event's live state line: `{home} h–a {away} · Corners h–a · clock`.
 * Mirrors the Live-match-events header derivation; renders exactly what the
 * feed provides (never a guessed score).
 */
export function formatStateLine(
  state: MatchState,
  homeTeam: string,
  awayTeam: string,
  clockText: string,
): string {
  const score = `${homeTeam} ${state.homeScore}–${state.awayScore} ${awayTeam}`;
  const corners = `Corners ${state.homeCorners}–${state.awayCorners}`;
  return `${score} · ${corners} · ${clockText}`;
}

// ─── line validation (config-time invariant, mirrored client-side) ───────────

/** Half-integer (`X.5`) — the config-time invariant. Integer lines are rejected. */
export function isHalfInteger(line: number): boolean {
  return Number.isFinite(line) && Math.abs(line - Math.floor(line) - 0.5) < 1e-9;
}

// ─── decimal text input normalization (shared by the import dialog) ──────────

/**
 * Normalize an in-progress decimal text field so the operator can freely type
 * `0.5`, `10`, `0,95`, `2.5` (comma = Spanish locale decimal separator). Pure +
 * framework-free so it is unit-testable and reusable by the controlled
 * `DecimalField`:
 *   - comma → dot (`"0,5"` → `"0.5"`),
 *   - strip anything that is not a digit or a dot (`"abc0.5x"` → `"0.5"`),
 *   - keep only the FIRST dot and everything up to the SECOND one, dropping the
 *     rest (`"0.5.3"` → `"0.5"`).
 * An in-progress `"0."` is PRESERVED (not coerced to `"0"`), which is exactly
 * what let the trailing dot survive so `0.5` is reachable.
 */
export function normalizeDecimalInput(raw: string): string {
  const stripped = raw.replace(/,/g, '.').replace(/[^0-9.]/g, '');
  const parts = stripped.split('.');
  if (parts.length <= 2) return stripped;
  return `${parts[0]}.${parts[1]}`;
}

// ─── import candidate → editable draft ───────────────────────────────────────

/**
 * Default confidence gate for a new market. Goals arrive at 100% and corners
 * can arrive unconfirmed, so BOTH default to a fire-safe 100% (`1`) — the
 * operator then lowers/raises per market. Kept as a function (not a constant)
 * so a future per-event-type default is a one-line change.
 */
export const DEFAULT_CONFIDENCE_GATE = 1;
export function defaultConfidenceGate(): number {
  return DEFAULT_CONFIDENCE_GATE;
}

/** Sensible default sweep ceiling (worst per-contract price), dollars. */
export const DEFAULT_MAX_PRICE = 0.99;

/**
 * Build an editable draft from a scraped candidate. A guessed `counter`/`line`
 * becomes an editable default; an UNCLASSIFIED candidate (`null` guess) leaves
 * `counter`/`line` UNSET (`null`) — NEVER silently defaulted to `match_goals`,
 * which would arm/fire the market on goals. Such a draft is INCOMPLETE (see
 * `isDraftComplete`) so the operator must pick a counter before it can be
 * persisted. `enabled` defaults false (fail-safe OFF), `size` defaults 10 (a
 * sensible operator default so a classified total is addable without extra
 * typing — markets are still created DISABLED / fail-safe OFF).
 */
export function candidateToDraft(
  candidate: SniperMarketCandidate,
): SniperMarketDraft {
  // The backend nests the auto-guess under `guess: {counter, team, line}`;
  // older shapes returned them flat. Read from either so the guessed
  // counter/line ALWAYS map (otherwise every row shows unset — the operator
  // would have to re-type the line by hand).
  const g = candidate.guess ?? candidate;
  return {
    venue: candidate.venue,
    marketId: candidate.marketId,
    marketLabel: candidate.marketLabel,
    counter: g.counter ?? null,
    team: g.team ?? null,
    line: g.line ?? null,
    buySide: 'over',
    size: 10,
    confidenceGate: defaultConfidenceGate(),
    maxPrice: DEFAULT_MAX_PRICE,
    enabled: false,
  };
}

/**
 * The first trading-critical field a draft is missing, as a short operator
 * label — or `null` when the draft is complete. Drives the "incomplete · X"
 * chip so the operator sees WHY a row can't be saved (e.g. "set a size", not a
 * silent block on the disabled Team field). Order mirrors `isDraftComplete`.
 */
export function draftIncompleteReason(draft: SniperMarketDraft): string | null {
  if (draft.counter == null) return 'pick a counter';
  if (draft.line == null || !isHalfInteger(draft.line)) {
    return 'invalid line (needs X.5)';
  }
  if (!(draft.size > 0)) return 'set a size';
  const teamRequired =
    draft.counter === 'team_goals' || draft.counter === 'team_corners';
  if (teamRequired && draft.team == null) return 'pick a team';
  if (!teamRequired && draft.team != null) return 'remove team (match-wide)';
  if (!(draft.maxPrice > 0) || draft.maxPrice > 1) return 'max price 0–1';
  if (!(draft.confidenceGate >= 0 && draft.confidenceGate <= 1)) {
    return 'confidence 0–100%';
  }
  return null;
}

/**
 * A draft is persistable only when every trading-critical field resolves:
 * an explicit counter, a half-integer line, a positive size, a team iff the
 * counter is `team_*`, and a 0..1 gate + positive maxPrice. Mirrors the
 * server-side validation so the operator can't POST a guess that would be
 * rejected. Single source of truth: `draftIncompleteReason`.
 */
export function isDraftComplete(draft: SniperMarketDraft): boolean {
  return draftIncompleteReason(draft) === null;
}
